import { type ReactNode } from 'react';
import Box from '@mui/material/Box';
import FormHelperText from '@mui/material/FormHelperText';
import InputLabel from '@mui/material/InputLabel';
import OutlinedInput from '@mui/material/OutlinedInput';
import { type SxProps, type Theme } from '@mui/material/styles';
import clsx from 'clsx';

/** Props for {@link OutlinedControl}. */
export type OutlinedControlProps = {
  readonly label: string;
  readonly helperText?: ReactNode;
  readonly error?: boolean;
  readonly disabled?: boolean;
  readonly className?: string;
  readonly sx?: SxProps<Theme>;
  readonly children: ReactNode;
};

/**
 * Notched outlined frame for non-text controls (sliders, checkbox lists, custom editors).
 * Helper text renders below the outline, matching MUI `TextField`.
 * @param props Component props.
 * @param props.label Short notched label.
 * @param props.helperText Optional caption below the outline.
 * @param props.error When true, styles the helper text as an error.
 * @param props.disabled When true, dims the outline.
 * @param props.className Optional class name merged onto the root.
 * @param props.sx Additional root `sx`.
 * @param props.children The slider, list, or other control to wrap.
 * @returns The framed control with optional helper text below.
 */
export default function OutlinedControl({
  label,
  helperText,
  error = false,
  disabled = false,
  className,
  sx,
  children,
}: OutlinedControlProps) {
  'use memo';

  return (
    <Box className={clsx('outlined-control', className)} sx={sx}>
      <Box sx={{ position: 'relative', pt: 1 }}>
        <InputLabel variant="outlined" shrink disabled={disabled} error={error}>
          {label}
        </InputLabel>
        <OutlinedInput
          label={label}
          notched
          readOnly
          tabIndex={-1}
          disabled={disabled}
          error={error}
          sx={{
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            '& .MuiOutlinedInput-input': { display: 'none' },
          }}
        />
        <Box sx={{ px: 4, pb: 2 }}>{children}</Box>
      </Box>
      {helperText ? (
        <FormHelperText error={error} disabled={disabled}>
          {helperText}
        </FormHelperText>
      ) : null}
    </Box>
  );
}
